import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { FormContainer, TextFieldElement, useForm } from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import { CREATE_BRAND_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Brand } from '@barboxx/models';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateBrandDialog = ({ onClose, onSuccess, open }: Props) => {
    const notifications = useNotifications();

    const formContext = useForm<Brand.CreateRequest>({
        resolver: yupResolver(CREATE_BRAND_SCHEMA),
        defaultValues: {},
    });

    const mutateCreateBrand = useMutation({
        mutationKey: ['brand'],
        mutationFn: (data: Brand.CreateRequest) =>
            Api.Products.Brands.postBrand(data),
    });

    const onSubmit = async (values: Brand.CreateRequest) => {
        return mutateCreateBrand
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created ${values.name}!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating brand!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancelCreate}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">Create Brand</DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <TextFieldElement label="Name" name="name" fullWidth />
                    <TextFieldElement
                        label="Description"
                        name="description"
                        fullWidth
                    />
                    <TextFieldElement
                        label="Website"
                        name="website"
                        fullWidth
                    />
                    <TextFieldElement
                        label="Manufacturer"
                        name="manufacturer"
                        fullWidth
                    />
                    <TextFieldElement
                        label="Country of Origin"
                        name="countryOfOrigin"
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
